/**
 * FaS WebAuthn SDK - React Integration
 */

import { useState, useEffect, useCallback } from 'react';
import FaSSDK from './index.js';

export function useFaSAuth(config) {
  const [sdk, setSdk] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (config?.clientId && config?.clientSecret) {
      const sdkInstance = new FaSSDK(config);
      setSdk(sdkInstance);
      if (sdkInstance.isAuthenticated()) {
        setUser({ authenticated: true });
      }
    }
  }, [config]);

  const register = useCallback(async (email, fullname) => {
    if (!sdk) throw new Error('SDK not initialized');
    setLoading(true);
    setError(null);
    try {
      const result = await sdk.registerPasskey(email, fullname);
      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  const login = useCallback(async (email) => {
    if (!sdk) throw new Error('SDK not initialized');
    setLoading(true);
    setError(null);
    try {
      const result = await sdk.authenticatePasskey(email);
      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  const passwordlessLogin = useCallback(async () => {
    if (!sdk) throw new Error('SDK not initialized');
    setLoading(true);
    setError(null);
    try {
      const result = await sdk.passwordlessLogin();
      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  const logout = useCallback(() => {
    if (sdk) {
      sdk.logout();
      setUser(null);
      setError(null);
    }
  }, [sdk]);

  return {
    user, loading, error, register, login, passwordlessLogin, logout,
    isAuthenticated: user !== null,
    isWebAuthnSupported: FaSSDK.isWebAuthnSupported()
  };
} 